import { useTranslation } from "@i18n";
import { renderUserTag } from "@CubeComponents/html/userTagDom";
import React, { useRef, useMemo, forwardRef } from "react";
import { convertDate } from "@Services/luxonHelper";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import img403 from "@Assets/images/noAccess.svg";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import FormFlowLibraryPopup from "./formFlowLibraryPopup";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { formFlowServiceUrl } from "@Services/dataManager";
import _ from "lodash";
import { getUserSelection } from "../CreateInspectionAndProgress/formsHelpers";
import Text from "@CubeComponents/html/Text/Text";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getProjectSettingsPageUrl } from "@Features/projects";
import SelectSpaces from "@Components/WorkspaceList/SelectSpaces";
import useFormFlowList from "./useFormFlowList";
import type { FormFlowRow } from "./formFlow.types";

const FormFlows = forwardRef(
  ({ projectId, setFormFlowId, showOnlyGrid = false, customQuery, setNextBtnState, customService }: any, ref: any) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [urlSearchParams] = useSearchParams();
    const formCategory = urlSearchParams.get("resourceType")?.toUpperCase();

    const tempRef = useRef<any>(null);
    const dataGrid = ref || tempRef;
    const archivedDataGrid = useRef<any>(null);

    /** Reads a grid's current selection as row data. */
    const getRowsOf = (gridRef: any): FormFlowRow[] =>
      gridRef.current?.slickGrid.getSelectedRows().map((row: any) => gridRef.current.slickGrid.getDataItem(row)) ?? [];

    const {
      hasAddPermission,
      listQuery,
      listService,
      archivedListQuery,
      columns,
      toolbarOptions,
      archivedToolbarOptions,
      createMenuItems,
      isLoadingComplete,
      showIllustration,
      archivedIsLoadingComplete,
      archivedShowIllustration,
      handleListLoaded,
      handleArchivedListLoaded,
      handleSelectionChange,
      handleArchivedSelectionChange,
      handleRecordClick,
      openAddEditModal,
      showDeleteConfirmationModal,
      showArchiveModal,
      addFromLibrary,
      setAddFromLibrary,
      openCreateModal,
      openLibraryModal,
      openArchiveModal,
      closeAddEditModal,
      closeDeleteModal,
      closeArchiveModal,
      handleCancelLibrary,
      handleCreateMenuSelect,
      editMode,
      formFlowData,
      formTemplates,
      setSpaceId,
      handleChange,
      isSaveDisabled,
      handleCreateFormFlowClick,
      userSelectionMode,
      handleUserSelectionModeChange,
      toolbarClickHandler,
      archivedToolbarClickHandler,
      handleDeleteFormFlow,
      refreshGrid,
      handleBackClick,
    } = useFormFlowList({
      projectId,
      formCategory,
      customQuery,
      serviceUrl: formFlowServiceUrl,
      customService,
      showOnlyGrid,
      setNextBtnState,
      setFormFlowId,
      getSelectedRows: () => getRowsOf(dataGrid),
      getArchivedSelectedRows: () => getRowsOf(archivedDataGrid),
      refreshList: () => dataGrid.current?.refresh(),
      refreshArchivedList: () => archivedDataGrid.current?.refresh(),
      onBack: () => navigate(getProjectSettingsPageUrl({ projectId, tab: "projectModules" })),
    });

    const createTemplate = () => {
      return (
        <SelectDropDown
          items={createMenuItems}
          select={(args: any) => handleCreateMenuSelect(args.item.id)}
          cssClass="e-caret-hide border-none ps-8 bg-white p-0"
        >
          <ButtonComponent id="add" buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
            <span className="flex gap-1 items-center justify-center">
              <Text>{t("Create")}</Text>
              <IconComponent name="arrowDownSm" width="1rem" height="1rem" />
            </span>
          </ButtonComponent>
        </SelectDropDown>
      );
    };

    /** The "Create" toolbar entry renders the split button; the rest are plain entries. */
    const gridToolbarOptions = toolbarOptions.map((option, index) =>
      index === 0 ? { ...option, template: createTemplate } : option,
    );

    const selectionEvent = (args: any) => {
      if (!dataGrid?.current) return;
      handleSelectionChange(args.detail.args.rows.length);
    };

    const archivedSelectionEvent = (args: any) => {
      if (!archivedDataGrid.current) return;
      handleArchivedSelectionChange(args.detail.args.rows.length);
    };

    const recordClick = (args: any) => {
      const rowIndex = args.detail.args.row;
      handleRecordClick({
        rowData: dataGrid.current.slickGrid.getDataItem(rowIndex),
        cell: args.detail.args.cell,
      });
    };

    const formatCreatedDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: FormFlowRow,
    ) => {
      return convertDate(Number(dataContext.createdDate), "dateForTooltip");
    };

    const createdByTemplate = (
      _row: number,
      _cell: number,
      _value: FormFlowRow["createdBy"],
      _columnDef: unknown,
      dataContext: FormFlowRow,
    ) => {
      const container = document.createElement("div");
      const _id = typeof dataContext.createdBy === "object" ? dataContext.createdBy?._id : dataContext.createdBy;
      renderUserTag(container, { id: _id });
      return container;
    };

    // Column metadata comes from the shared constants; only the cell renderers are web-specific.
    const columnDefinitions = useMemo(
      () =>
        columns.map((column) => {
          switch (column.id) {
            case "selectedForm":
              return {
                ...column,
                formatter: (
                  _row: number,
                  _cell: number,
                  _value: FormFlowRow["selectedForm"],
                  _columnDef: unknown,
                  dataContext: FormFlowRow,
                ) => _.get(dataContext, "selectedForm.name"),
                _valueGetter: (row: FormFlowRow) => row.selectedForm?.name,
              };
            case "space":
              return {
                ...column,
                formatter: (
                  _row: number,
                  _cell: number,
                  _value: FormFlowRow["space"],
                  _columnDef: unknown,
                  dataContext: FormFlowRow,
                ) => _.get(dataContext, "space.spaceName"),
                _valueGetter: (row: FormFlowRow) => row.space?.spaceName,
              };
            case "createdBy":
              return {
                ...column,
                formatter: createdByTemplate,
                _valueGetter: (row: FormFlowRow) =>
                  typeof row.createdBy === "object" ? row.createdBy?.fullName : row.createdBy,
              };
            case "createdDate":
              return { ...column, formatter: formatCreatedDate };
            default:
              return column;
          }
        }),
      [columns],
    );

    const headerContent = () => (
      <div className=" w-full flex justify-between rounded-md">
        <Text className="">{editMode ? t("Edit Form Flow") : t("Create New Form Flow")}</Text>
        {!editMode && (
          <SelectSpaces
            disablePermissionCheck
            data-testid="spaceDropdownField"
            projectId={projectId}
            selectedSpaceCallback={(params: { _id: string }) => {
              setSpaceId(params._id);
            }}
          />
        )}
      </div>
    );

    const modelContent = () => (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Text>{t("Form Flow Name")}</Text>
          <span>
            <TextFieldComponent
              name="name"
              type="text"
              placeholder={t("Add form flow name")}
              showClearButton
              onChange={handleChange}
              value={formFlowData.name}
            />
          </span>
        </div>
        <div className="flex flex-col gap-2">
          <Text>{t("Select Template")}</Text>
          <span>
            <SelectDropDown
              searchable
              value={formFlowData.selectedTemplate}
              placeholder={t("Select Form")}
              disabled={formFlowData.formCategory == "" || editMode}
              onChange={(e) => handleChange({ target: { name: "selectedTemplate", value: e } })}
              buttonType={BUTTON_TYPES.BORDER_BUTTON}
              fields={{
                text: "name",
                value: "_id",
              }}
              options={formTemplates}
              buttonWidth="100%"
              popperWidth={300}
            />
          </span>
        </div>
        {!editMode &&
          getUserSelection(
            "Assign users to level",
            userSelectionMode,
            handleUserSelectionModeChange as (args: React.ChangeEvent<HTMLInputElement>) => void,
          )}
      </div>
    );

    const footerContent = () => (
      <div className="flex gap-4 items-center mt-5">
        <ButtonComponent
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          disabled={isSaveDisabled}
          onClick={handleCreateFormFlowClick}
        >
          <Text>{editMode ? t("Update") : t("Create")}</Text>
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={closeAddEditModal}>
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );

    const getGridComponent = ({
      gridRef,
      query,
      onSelectionChange,
      toolbarClick,
      toolbar,
      onRowClick,
      onDataLoaded,
    }: {
      gridRef: any;
      query: any;
      onSelectionChange: (args: any) => void;
      toolbarClick: (params: any) => void;
      toolbar: any;
      onRowClick?: (args: any) => void;
      onDataLoaded: (count?: number) => void;
    }) => (
      <SlickGrid1
        ref={gridRef}
        columnDefinitions={columnDefinitions as any}
        enableSortingOption
        onSelectedRowIdsChanged={onSelectionChange}
        remoteDataSource={listService}
        infiniteScroll
        query={query}
        onClick={onRowClick}
        enableFilteringOption
        enableGroupingOption
        enableColumnChooser
        toolbarClickHandler={toolbarClick}
        toolbar={toolbar as any}
        setGridReady={(_ready, count) => onDataLoaded(count)}
      />
    );

    const emptyIllustration = (show: boolean, title: string, actions?: React.ReactNode) =>
      show ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 bg-white">
          <span className="flex flex-col items-center">
            <IconComponent name="NoData" className="h-52 w-52" />
            <Text className="text-2xl text-text_gray">{title}</Text>
          </span>
          {actions}
        </div>
      ) : null;

    const archiveModal = () => (
      <ModalStateDialogComponent
        allowModalPadding={false}
        width="80%"
        height="90%"
        headerTitle={<Text className="text-2xl font-semibold">{t("Archived Form Flows")}</Text>}
        isVisible={showArchiveModal}
        onCancel={closeArchiveModal}
        modelContent={
          <div className="relative flex w-full h-full overflow-auto">
            <div
              className={`${archivedIsLoadingComplete && !archivedShowIllustration ? "" : "invisible"} h-full w-full overflow-auto`}
            >
              {getGridComponent({
                gridRef: archivedDataGrid,
                query: archivedListQuery,
                onSelectionChange: archivedSelectionEvent,
                toolbarClick: archivedToolbarClickHandler,
                toolbar: archivedToolbarOptions,
                onDataLoaded: handleArchivedListLoaded,
              })}
            </div>
            {emptyIllustration(archivedIsLoadingComplete && archivedShowIllustration, t("No Archived Form Flows"))}
          </div>
        }
        footerContent={
          <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={closeArchiveModal}>
            <Text>{t("Close")}</Text>
          </ButtonComponent>
        }
      />
    );

    const addEditModal = () => (
      <ModalStateDialogComponent
        allowModalPadding={false}
        footerContent={footerContent()}
        width="50%"
        headerTitle={headerContent()}
        onCancel={closeAddEditModal}
        isVisible={openAddEditModal}
        modelContent={modelContent()}
      />
    );

    const deletePopup = () => (
      <ModalStateDialogComponent
        allowModalPadding={false}
        footerContent={
          <div className=" flex gap-4">
            <ButtonComponent onClick={handleDeleteFormFlow} buttonType="gradientButton">
              <Text>{t("Confirm")}</Text>
            </ButtonComponent>
            <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeDeleteModal}>
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
          </div>
        }
        headerTitle={
          <div className=" w-full flex justify-between rounded-md">
            <Text className="">{t("Delete selected form flow(s)")}</Text>
          </div>
        }
        isVisible={showDeleteConfirmationModal}
        onCancel={closeDeleteModal}
        modelContent={
          <Text className="whitespace-normal">{t("Are you sure you want to delete the selected form flow(s) ?")}</Text>
        }
        width="30%"
      />
    );

    const noAccessPage = () => (
      <div className="flex w-full h-full overflow-auto flex-col justify-center items-center gap-1">
        <img src={img403} alt="noAccess" />
        <Text className="font-semibold text-2xl">{t("You do not have access to this page")}</Text>
      </div>
    );

    const header = () => {
      return (
        <div className="flex justify-between items-center ps-2 pt-4 pb-2 pe-4 w-full">
          <div className="flex gap-4 items-center">
            <IconComponent className="cursor-pointer" onClick={handleBackClick} name="BackwardArrow" />
            <div className="flex gap-1 flex-col">
              <Text className="text-2xl font-semibold">{t("Form Flows")}</Text>
              <Text>{t("List of all the Form Flows in this Project")}</Text>
            </div>
          </div>
          <div onClick={openArchiveModal} className="flex gap-1 cursor-pointer items-center">
            <IconComponent className="h-4 w-4" name="archive" />
            <Text>{t("View Archived")}</Text>
          </div>
        </div>
      );
    };

    const getFormFlowContent = () => (
      <div className="flex w-full flex-col h-full overflow-auto">
        {!showOnlyGrid && header()}
        <div className="relative flex w-full h-full overflow-auto">
          <div className={`${isLoadingComplete && !showIllustration ? "" : "invisible"} h-full w-full overflow-auto`}>
            {getGridComponent({
              gridRef: dataGrid,
              query: listQuery,
              onSelectionChange: selectionEvent,
              toolbarClick: toolbarClickHandler,
              toolbar: !showOnlyGrid ? gridToolbarOptions : null,
              onRowClick: recordClick,
              onDataLoaded: handleListLoaded,
            })}
          </div>
          {emptyIllustration(
            isLoadingComplete && showIllustration,
            t("No Form Flow has been added yet"),
            !showOnlyGrid ? (
              <div className="flex flex-row gap-4">
                <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={openCreateModal}>
                  <span className="flex items-center">
                    <IconComponent className="h-4" name="add" />
                    <Text>{t("Create")}</Text>
                  </span>
                </ButtonComponent>
                <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={openLibraryModal}>
                  <span className="flex items-center">
                    <IconComponent className="h-4" name="add" />
                    <Text>{t("Create From Library")}</Text>
                  </span>
                </ButtonComponent>
              </div>
            ) : undefined,
          )}
        </div>
      </div>
    );

    const getFlowsFromLibrary = () => {
      return (
        <FormFlowLibraryPopup
          handleCancelibrary={handleCancelLibrary}
          projectId={projectId}
          refreshGrid={refreshGrid}
          addFromLibrary={addFromLibrary}
          setAddFromLibrary={setAddFromLibrary}
          resourceType={formFlowData.formCategory}
        />
      );
    };

    return (
      <>
        {hasAddPermission ? getFormFlowContent() : noAccessPage()}
        {openAddEditModal && addEditModal()}
        {showDeleteConfirmationModal && deletePopup()}
        {addFromLibrary.state && getFlowsFromLibrary()}
        {showArchiveModal && archiveModal()}
      </>
    );
  },
);
FormFlows.displayName = "FormFlows";
export default FormFlows;
